import React, { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import moment from "moment";
import {
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  InputAdornment,
  Chip,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useMediaQuery,
} from "@material-ui/core";
import { useTheme } from "@material-ui/core/styles";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import SearchIcon from "@material-ui/icons/Search";
import GetAppIcon from "@material-ui/icons/GetApp";
import FileCopyOutlinedIcon from "@material-ui/icons/FileCopyOutlined";
import PauseIcon from "@material-ui/icons/Pause";
import PlayArrowIcon from "@material-ui/icons/PlayArrow";
import SendIcon from "@material-ui/icons/Send";

import MainContainer from "../../components/MainContainer";
import Title from "../../components/Title";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { socketConnection } from "../../services/socket";
import formatPhone from "../../helpers/formatPhone";
import {
  useCampaignStyles,
  KpiStrip,
  CampaignStatusPill,
  ProgressBar,
  campaignNumbers,
  estimateSeconds,
  formatDuration,
  friendlyDateTime,
  paceSummary,
  parseCampaignSettings,
  toneColor,
  downloadCsv,
  MESSAGE_FIELDS,
  isCampaignStopped,
} from "../Campaigns/campaignShared";

// Prazo para responder à pergunta de confirmação (mesmo valor do backend,
// CONFIRMATION_REPLY_WINDOW_HOURS). Só define o rótulo "Sem resposta".
const REPLY_WINDOW_HOURS = 72;

// `onlyConfirmation`: aba que só faz sentido em campanha com confirmação;
// `noConfirmation`: só sem confirmação (com confirmação, quem recebeu a
// mensagem principal é quem confirmou, e a aba "Confirmaram" já mostra).
const FILTERS = [
  { key: "all", label: "Todos" },
  { key: "confirmed", label: "Confirmaram", onlyConfirmation: true },
  { key: "awaiting", label: "Aguardando resposta", onlyConfirmation: true },
  { key: "noReply", label: "Sem resposta", onlyConfirmation: true },
  { key: "refused", label: "Recusaram", onlyConfirmation: true },
  { key: "delivered", label: "Entregues", noConfirmation: true },
  { key: "queued", label: "Na fila" },
  { key: "invalid", label: "Sem WhatsApp" },
];

// Situação de cada contato na campanha
// Com confirmação, a pergunta enviada fica em confirmationRequestedAt e só a
// mensagem principal em deliveredAt; `confirmation` false é recusa.
const rowStatus = (r, campaign) => {
  if (!r.isWhatsappValid) return { label: "Sem WhatsApp", tone: "red" };
  if (r.confirmation === false) return { label: "Recusou", tone: "red" };
  if (r.confirmedAt) {
    return r.deliveredAt
      ? { label: "Confirmou", tone: "purple" }
      : { label: "Confirmou · enviando", tone: "blue" };
  }
  if (r.deliveredAt) return { label: "Entregue", tone: "green" };
  if (r.confirmationRequestedAt) {
    return moment().diff(moment(r.confirmationRequestedAt), "hours") >= REPLY_WINDOW_HOURS
      ? { label: "Sem resposta", tone: "grey" }
      : { label: "Aguardando resposta", tone: "blue" };
  }
  if (isCampaignStopped(campaign?.status)) return { label: "Não enviado", tone: "amber" };
  if (campaign?.status === "FINALIZADA") return { label: "Não enviado", tone: "grey" };
  return { label: "Na fila", tone: "grey" };
};

const pillClass = (tone, classes) =>
  ({ green: classes.pillGreen, red: classes.pillRed, amber: classes.pillAmber, grey: classes.pillGrey, blue: classes.pillBlue, purple: classes.pillPurple }[tone]);

const CampaignReport = () => {
  const classes = useCampaignStyles();
  const theme = useTheme();
  const history = useHistory();
  const { campaignId } = useParams();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [campaign, setCampaign] = useState(null);
  const [settings, setSettings] = useState(null);
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState(null);
  const [count, setCount] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [filter, setFilter] = useState("all");
  const [searchParam, setSearchParam] = useState("");
  const [loadingRows, setLoadingRows] = useState(true);
  const [busy, setBusy] = useState(false);
  const [confirmPause, setConfirmPause] = useState(false);

  const loadCampaign = useCallback(async () => {
    try {
      const { data } = await api.get(`/campaigns/${campaignId}`);
      setCampaign(data);
    } catch (err) {
      toastError(err);
    }
  }, [campaignId]);

  const loadRows = useCallback(
    async (page, reset) => {
      setLoadingRows(true);
      try {
        const { data } = await api.get(`/campaigns/${campaignId}/shippings`, {
          params: { filter, searchParam, pageNumber: page },
        });
        setRows((prev) => (reset ? data.records : [...prev, ...data.records]));
        setCounts(data.counts);
        setCount(data.count);
        setHasMore(data.hasMore);
      } catch (err) {
        toastError(err);
      }
      setLoadingRows(false);
    },
    [campaignId, filter, searchParam]
  );

  useEffect(() => {
    loadCampaign();
    api
      .get("/campaign-settings")
      .then(({ data }) => setSettings(parseCampaignSettings(data)))
      .catch(() => {});
  }, [loadCampaign]);

  useEffect(() => {
    setPageNumber(1);
    const t = setTimeout(() => loadRows(1, true), 300);
    return () => clearTimeout(t);
  }, [loadRows]);

  // Atualiza ao vivo enquanto envia (cada envio emite um aviso; agrupa em 3 s)
  const liveTimer = useRef(null);
  useEffect(() => {
    const companyId = localStorage.getItem("companyId");
    const socket = socketConnection({ companyId });
    socket.on(`company-${companyId}-campaign`, (data) => {
      if (data.action === "delete" && +data.id === +campaignId) {
        toast.info("Esta campanha foi excluída");
        history.push("/campaigns");
        return;
      }
      if (data.action === "update" && data.record?.id === +campaignId) {
        clearTimeout(liveTimer.current);
        liveTimer.current = setTimeout(() => {
          loadCampaign();
          loadRows(1, true);
          setPageNumber(1);
        }, 3000);
      }
    });
    return () => {
      clearTimeout(liveTimer.current);
      socket.disconnect();
    };
  }, [campaignId, history, loadCampaign, loadRows]);

  const loadMore = () => {
    const next = pageNumber + 1;
    setPageNumber(next);
    loadRows(next, false);
  };

  const handleScroll = (e) => {
    if (!hasMore || loadingRows) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) loadMore();
  };

  const act = async (fn, msg) => {
    setBusy(true);
    try {
      await fn();
      toast.success(msg);
      await loadCampaign();
      loadRows(1, true);
    } catch (err) {
      toastError(err);
    }
    setBusy(false);
  };

  const duplicate = async () => {
    setBusy(true);
    try {
      await api.post(`/campaigns/${campaignId}/duplicate`);
      toast.success("Cópia criada como rascunho em Campanhas");
      history.push("/campaigns");
    } catch (err) {
      toastError(err);
    }
    setBusy(false);
  };

  const exportCsv = async () => {
    try {
      const { data } = await api.get(`/campaigns/${campaignId}/shippings`, { params: { filter: "all", all: 1 } });
      downloadCsv(
        `campanha-${campaignId}-envios.csv`,
        ["Nome", "Número", "Situação", "Entregue em", "Confirmou em", "Mensagem"],
        data.records.map((r) => [
          r.name,
          r.number,
          rowStatus(r, campaign).label,
          r.deliveredAt ? moment(r.deliveredAt).format("DD/MM/YYYY HH:mm") : "",
          r.confirmedAt ? moment(r.confirmedAt).format("DD/MM/YYYY HH:mm") : "",
          (r.message || "").replace(/^‌\s*/, ""),
        ])
      );
    } catch (err) {
      toastError(err);
    }
  };

  if (!campaign) {
    return (
      <MainContainer>
        <div className={classes.root}>
          <Button className={classes.backLink} startIcon={<ArrowBackIcon fontSize="small" />} onClick={() => history.push("/campaigns")}>
            Campanhas
          </Button>
          <Typography variant="body2" className={classes.muted}>Carregando…</Typography>
        </div>
      </MainContainer>
    );
  }

  const n = campaignNumbers(campaign);
  const c = counts || {};
  const valid = c.valid ?? n.valid;
  const delivered = c.delivered ?? n.delivered;
  const confirmed = c.confirmed ?? n.confirmed;
  const requested = c.confirmationRequested ?? n.requested;
  const queued = c.queued ?? n.queued;
  const invalid = c.invalid ?? n.invalid;
  const pct = valid > 0 ? Math.round((delivered / valid) * 100) : 0;
  const running = campaign.status === "EM_ANDAMENTO";
  const startsLater = running && campaign.scheduledAt && moment(campaign.scheduledAt).isAfter(moment());
  const left = settings && queued > 0 ? estimateSeconds(queued, settings) : null;

  const subtitleParts = [];
  if (startsLater) subtitleParts.push(`Começa ${friendlyDateTime(campaign.scheduledAt).toLowerCase()}`);
  else if (campaign.scheduledAt) subtitleParts.push(`Iniciada ${friendlyDateTime(campaign.scheduledAt).toLowerCase()}`);
  if (campaign.whatsapp) subtitleParts.push(`Conexão ${campaign.whatsapp.name}`);
  if (campaign.contactList) subtitleParts.push(`Lista ${campaign.contactList.name}`);

  const kpis = [
    { label: "Público", tone: "grey", value: (c.total ?? n.total).toLocaleString("pt-BR"), caption: `${valid} com WhatsApp` },
    {
      label: "Entregues",
      tone: "green",
      value: delivered.toLocaleString("pt-BR"),
      caption: campaign.confirmation
        ? `receberam a mensagem principal (${pct}%)`
        : `${pct}% do público válido`,
    },
    {
      label: "Na fila",
      tone: "blue",
      value: running || isCampaignStopped(campaign.status) ? queued.toLocaleString("pt-BR") : "—",
      caption:
        campaign.status === "PAUSADA_SEM_CREDITO"
          ? "aguardando recarga de créditos"
          : campaign.status === "CANCELADA"
          ? "aguardando retomar"
          : running && left !== null
          ? `~${formatDuration(left)} para terminar`
          : "",
    },
    campaign.confirmation
      ? {
          label: "Confirmações",
          tone: "amber",
          value: confirmed.toLocaleString("pt-BR"),
          caption:
            requested > 0
              ? [
                  `${Math.round((confirmed / requested) * 100)}% de quem recebeu a pergunta`,
                  c.awaiting ? `${c.awaiting} aguardando` : null,
                  c.refused ? `${c.refused} recusaram` : null,
                ]
                  .filter(Boolean)
                  .join(" · ")
              : "",
        }
      : { label: "Sem WhatsApp", tone: "red", value: invalid.toLocaleString("pt-BR"), caption: "ficam de fora", colorValue: invalid > 0 },
  ];

  const greenPct = valid > 0 ? ((delivered - confirmed) / valid) * 100 : 0;
  const purplePct = valid > 0 ? (confirmed / valid) * 100 : 0;
  const variations = MESSAGE_FIELDS.map((f) => campaign[f]).filter((m) => m && m.trim());

  const headerActions = [];
  if (!isMobile)
    headerActions.push(
      <Button key="csv" variant="outlined" className={`${classes.headerButton} ${classes.secondaryButton}`} startIcon={<GetAppIcon />} onClick={exportCsv}>
        Exportar CSV
      </Button>
    );
  headerActions.push(
    <Button key="dup" variant="outlined" disabled={busy} className={`${classes.headerButton} ${classes.secondaryButton}`} startIcon={<FileCopyOutlinedIcon />} onClick={duplicate}>
      Duplicar
    </Button>
  );
  if (running)
    headerActions.push(
      <Button
        key="pause"
        variant="contained"
        disableElevation
        disabled={busy}
        className={classes.headerButton}
        style={{ backgroundColor: toneColor(theme, "amber"), color: "#fff" }}
        startIcon={<PauseIcon />}
        onClick={() => setConfirmPause(true)}
      >
        Pausar envio
      </Button>
    );
  if (isCampaignStopped(campaign.status))
    headerActions.push(
      <Button
        key="resume"
        variant="contained"
        color="primary"
        disableElevation
        disabled={busy}
        className={classes.headerButton}
        startIcon={<PlayArrowIcon />}
        onClick={() => act(() => api.post(`/campaigns/${campaignId}/restart`), "Envio retomado")}
      >
        Retomar envio
      </Button>
    );
  if (campaign.status === "PROGRAMADA")
    headerActions.push(
      <Button
        key="now"
        variant="contained"
        color="primary"
        disableElevation
        disabled={busy}
        className={classes.headerButton}
        startIcon={<SendIcon />}
        onClick={() => act(() => api.post(`/campaigns/${campaignId}/send-now`), "Envio iniciado")}
      >
        Enviar agora
      </Button>
    );

  const emptyText = {
    all: searchParam ? "Nenhum contato com esse nome ou número" : "Nenhum contato na lista desta campanha",
    confirmed: "Ninguém confirmou ainda",
    delivered: "Nenhuma mensagem entregue ainda",
    queued: "Nenhuma mensagem na fila",
    invalid: "Todos os contatos têm WhatsApp",
  }[filter];

  return (
    <MainContainer>
      <Dialog open={confirmPause} onClose={() => setConfirmPause(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Pausar o envio?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" className={classes.muted}>
            As mensagens que ainda estão na fila não serão enviadas. Você pode retomar depois — quem já recebeu não recebe de novo.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmPause(false)}>Voltar</Button>
          <Button
            variant="contained"
            color="primary"
            disableElevation
            onClick={() => {
              setConfirmPause(false);
              act(() => api.post(`/campaigns/${campaignId}/cancel`), "Campanha pausada");
            }}
          >
            Pausar
          </Button>
        </DialogActions>
      </Dialog>

      <div className={classes.root} style={{ overflowY: "auto" }}>
        <div>
          <Button className={classes.backLink} startIcon={<ArrowBackIcon fontSize="small" />} onClick={() => history.push("/campaigns")}>
            Campanhas
          </Button>
        </div>

        <div className={classes.header}>
          <div className={classes.headerTitle} style={{ minWidth: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
              <Title>{campaign.name}</Title>
              <CampaignStatusPill
                status={campaign.status}
                classes={classes}
                label={running ? (startsLater ? "Agendada" : "Enviando · ao vivo") : undefined}
              />
            </div>
            <Typography variant="body2" className={classes.muted}>
              {subtitleParts.join(" · ")}
            </Typography>
          </div>
          <div className={classes.headerButtons}>{headerActions}</div>
        </div>

        <div className={classes.card} style={{ padding: theme.spacing(2, 2.5), marginBottom: theme.spacing(1.75) }}>
          <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 8, fontSize: 14 }}>
            <span>
              <b style={{ fontSize: 20 }}>{delivered.toLocaleString("pt-BR")}</b>{" "}
              <span className={classes.muted}>de {valid.toLocaleString("pt-BR")} mensagens entregues</span>
            </span>
            <span className={classes.muted}>
              {pct}%
              {running && !startsLater && left !== null && ` · termina em ~${formatDuration(left)} (por volta de ${moment().add(left, "seconds").format("HH:mm")})`}
              {campaign.status === "FINALIZADA" && campaign.completedAt && ` · concluída ${friendlyDateTime(campaign.completedAt).toLowerCase()}`}
            </span>
          </div>
          <ProgressBar
            classes={classes}
            height={10}
            segments={
              campaign.confirmation
                ? [
                    { pct: purplePct, color: toneColor(theme, "purple") },
                    { pct: greenPct, color: toneColor(theme, "green") },
                  ]
                : [{ pct, color: toneColor(theme, campaign.status === "PAUSADA_SEM_CREDITO" ? "red" : campaign.status === "CANCELADA" ? "amber" : "green") }]
            }
          />
          <div style={{ display: "flex", flexWrap: "wrap", gap: 16, marginTop: 8 }} className={classes.subtle}>
            {campaign.confirmation && (
              <span>
                <span className={classes.legendSwatch} style={{ backgroundColor: toneColor(theme, "purple") }} />
                Confirmaram ({confirmed})
              </span>
            )}
            <span>
              <span className={classes.legendSwatch} style={{ backgroundColor: toneColor(theme, "green") }} />
              {campaign.confirmation ? `Entregues, sem resposta (${delivered - confirmed})` : `Entregues (${delivered})`}
            </span>
            <span>
              <span className={classes.legendSwatch} style={{ backgroundColor: theme.palette.divider }} />
              {campaign.status === "PAUSADA_SEM_CREDITO" ? "Sem créditos" : campaign.status === "CANCELADA" ? "Pausadas" : "Na fila"} ({queued})
            </span>
          </div>
        </div>

        <KpiStrip items={kpis} classes={classes} theme={theme} />

        <div style={{ display: "flex", flexWrap: "wrap", gap: 16, alignItems: "flex-start" }}>
          <Paper className={classes.mainPaper} variant="outlined" style={{ flex: "999 1 560px", minWidth: 0, maxHeight: 640 }}>
            <div className={classes.toolbar} style={isMobile ? { padding: theme.spacing(1.5) } : undefined}>
              <Typography style={{ fontWeight: 600, marginRight: "auto" }}>Envios por contato</Typography>
              <TextField
                variant="outlined"
                size="small"
                placeholder="Nome ou número"
                type="search"
                value={searchParam}
                onChange={(e) => setSearchParam(e.target.value)}
                className={classes.searchField}
                style={{ maxWidth: 220 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" fontSize="small" />
                    </InputAdornment>
                  ),
                }}
                inputProps={{ "aria-label": "Buscar contato" }}
              />
              <div className={`${classes.chipGroup} ${classes.chipGroupScroll}`} style={{ width: "100%" }} role="group" aria-label="Situação">
                {FILTERS.filter((f) => (campaign.confirmation ? !f.noConfirmation : !f.onlyConfirmation)).map((f) => {
                  const active = filter === f.key;
                  const num = counts
                    ? {
                        all: counts.total,
                        confirmed: counts.confirmed,
                        awaiting: counts.awaiting,
                        noReply: counts.noReply,
                        refused: counts.refused,
                        delivered: counts.delivered,
                        queued: counts.queued,
                        invalid: counts.invalid,
                      }[f.key]
                    : null;
                  return (
                    <Chip
                      key={f.key}
                      clickable
                      className={classes.filterChip}
                      label={num !== null && num !== undefined ? `${f.label} ${num}` : f.label}
                      color={active ? "primary" : "default"}
                      variant={active ? "default" : "outlined"}
                      aria-pressed={active}
                      onClick={() => setFilter(f.key)}
                    />
                  );
                })}
              </div>
            </div>
            <div className={classes.tableWrapper} onScroll={handleScroll}>
              {isMobile ? (
                <div>
                  {rows.map((r) => {
                    const st = rowStatus(r, campaign);
                    return (
                      <div key={r.id} className={classes.mobileRow}>
                        <div className={classes.mobileInfo}>
                          <div className={classes.mobileTitle}>{r.name || formatPhone(r.number)}</div>
                          <div className={classes.subtle}>{formatPhone(r.number)}</div>
                        </div>
                        <div className={classes.mobileValue}>
                          <Chip size="small" label={st.label} className={`${classes.pill} ${pillClass(st.tone, classes)}`} />
                          {r.deliveredAt && <span className={classes.subtle}>{moment(r.deliveredAt).format("DD/MM HH:mm")}</span>}
                        </div>
                      </div>
                    );
                  })}
                  {!loadingRows && rows.length === 0 && <div className={classes.emptyState}>{emptyText}</div>}
                </div>
              ) : (
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell className={classes.headCell} style={{ paddingLeft: 20 }}>Contato</TableCell>
                      <TableCell className={classes.headCell}>Situação</TableCell>
                      <TableCell className={classes.headCell}>Mensagem enviada</TableCell>
                      <TableCell className={classes.headCell} align="right" style={{ paddingRight: 20 }}>Horário</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.map((r) => {
                      const st = rowStatus(r, campaign);
                      const msg = (r.message || "").replace(/^‌\s*/, "");
                      return (
                        <TableRow key={r.id} className={classes.row}>
                          <TableCell style={{ paddingLeft: 20 }}>
                            <div style={{ fontWeight: 600 }}>{r.name || "—"}</div>
                            <div className={classes.subtle}>{formatPhone(r.number)}</div>
                          </TableCell>
                          <TableCell>
                            <Chip size="small" label={st.label} className={`${classes.pill} ${pillClass(st.tone, classes)}`} />
                          </TableCell>
                          <TableCell style={{ maxWidth: 280 }}>
                            <div className={classes.subtle} style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={msg}>
                              {msg || "—"}
                            </div>
                          </TableCell>
                          <TableCell align="right" style={{ paddingRight: 20, whiteSpace: "nowrap" }}>
                            {r.deliveredAt ? moment(r.deliveredAt).format("DD/MM HH:mm") : <span className={classes.subtle}>—</span>}
                            {r.confirmedAt && <div className={classes.subtle}>resp. {moment(r.confirmedAt).format("HH:mm")}</div>}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    {loadingRows && <TableRowSkeleton columns={4} />}
                    {!loadingRows && rows.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={4}>
                          <div className={classes.emptyState}>{emptyText}</div>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              )}
            </div>
            {rows.length > 0 && (
              <div className={classes.footer}>
                Mostrando {rows.length} de {count}
                {hasMore ? " · role para carregar mais" : ""}
              </div>
            )}
          </Paper>

          <div style={{ flex: "1 1 280px", minWidth: 0, display: "flex", flexDirection: "column", gap: 16 }}>
            <div className={classes.card} style={{ padding: theme.spacing(2, 2.25), display: "flex", flexDirection: "column", gap: 10 }}>
              <div className={classes.cardTitle}>Detalhes</div>
              <div className={classes.detailRow}><span>Conexão</span><span>{campaign.whatsapp?.name || "—"}</span></div>
              <div className={classes.detailRow}><span>Público</span><span>{campaign.contactList?.name || "—"}</span></div>
              <div className={classes.detailRow}><span>Agendada para</span><span>{campaign.scheduledAt ? moment(campaign.scheduledAt).format("DD/MM/YYYY, HH:mm") : "—"}</span></div>
              <div className={classes.detailRow}>
                <span>Concluída em</span>
                <span style={!campaign.completedAt ? { fontWeight: 400 } : undefined}>
                  {campaign.completedAt ? moment(campaign.completedAt).format("DD/MM/YYYY, HH:mm") : running ? "ainda enviando" : "—"}
                </span>
              </div>
              {settings && <div className={classes.detailRow}><span>Ritmo</span><span>{paceSummary(settings)}</span></div>}
              <div className={classes.detailRow}><span>Pede confirmação</span><span>{campaign.confirmation ? "Sim" : "Não"}</span></div>
              {campaign.mediaName && <div className={classes.detailRow}><span>Anexo</span><span>{campaign.mediaName}</span></div>}
            </div>

            <div className={classes.card} style={{ padding: theme.spacing(2, 2.25), display: "flex", flexDirection: "column", gap: 10 }}>
              <div className={classes.cardTitle}>Mensagens</div>
              {campaign.confirmation && campaign.confirmationMessage1 && (
                <>
                  <div className={classes.subtle} style={{ fontWeight: 600 }}>Pergunta de confirmação</div>
                  <div className={classes.messageBox}>{campaign.confirmationMessage1}</div>
                </>
              )}
              {variations.length === 0 && <div className={classes.subtle}>Nenhuma mensagem escrita.</div>}
              {variations.map((m, i) => (
                <React.Fragment key={i}>
                  <div className={classes.subtle} style={{ fontWeight: 600 }}>
                    {variations.length > 1 ? `Variação ${i + 1}` : "Mensagem"}
                  </div>
                  <div className={classes.messageBox}>{m}</div>
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>
      </div>
    </MainContainer>
  );
};

export default CampaignReport;
